import {
  ControlElement,
  GroupLayout,
  JsonSchema,
  LabelDescription,
  Layout,
  UISchemaElement,
} from '@jsonforms/core';
import { cloneDeep, get, isBoolean } from 'lodash';
import currencies from '../widgets/DynamicForm/renderers/controls/CurrencyInput/currency.json';
import { ErrorSchema, TruAction, TruRule } from '../types';
export const getElements = (uischema: UISchemaElement): UISchemaElement[] => {
  const elements = (uischema as GroupLayout).elements;

  if (!elements) {
    console.error('Trying to access elements on uischema', uischema);
    throw new Error('uischema has no elements property as expected');
  }

  return (uischema as GroupLayout).elements;
};

export const getLabel = (element: UISchemaElement): string => {
  return (element as { label?: string })?.label || '';
};

export const getActions = (element: UISchemaElement): TruAction[] | null => {
  const actions = (element as unknown as { actions: TruAction[] }).actions;

  if (!actions) {
    return null;
  }

  return actions.length ? actions : null;
};

export const getRule = (element: UISchemaElement): TruRule | null => {
  if (!element.rule) return null;

  return element.rule as TruRule;
};

const filterUiSchemaControls = (
  uiSchema: JsonSchema,
  value: string,
): JsonSchema => {
  const uiSchemaCopy = cloneDeep(uiSchema) as any;
  filterControls(uiSchemaCopy, value);
  return uiSchemaCopy;
};

const controls = [
  'AutoCompleteSearch',
  'ButtonControl',
  'Currency',
  'DurationDatePicker',
  'FormArray',
  'Control',
  'RangeDate',
  'SelectWithApiData',
  'booleanSelect',
];

const filterControls = (layout: Layout, value: string): void => {
  layout.elements &&
    layout.elements.forEach((element) => {
      if (controls.includes(element.type)) {
        const control = element as ControlElement;

        const isLabelMatch = isControlLabelMatch(control, value);

        if (!isLabelMatch) {
          layout.elements = layout.elements.filter(
            (elem) => (elem as ControlElement).scope !== control.scope,
          );
        }
      } else {
        filterControls(element as Layout, value);
      }
    });
};

export const isControlLabelMatch = (
  control: ControlElement,
  value: string,
): boolean => {
  const trimmedLowerCaseValue = value.trim().toLowerCase();
  if (!trimmedLowerCaseValue) return true;

  const label = control.label;

  if (!label || isBoolean(label))
    return isControlNameMatch(control, trimmedLowerCaseValue);

  if ((label as LabelDescription).text)
    return ((label as LabelDescription).text as string)
      .toLowerCase()
      .includes(trimmedLowerCaseValue);

  return (label as string).toLowerCase().includes(trimmedLowerCaseValue);
};

const isControlNameMatch = (
  control: ControlElement,
  value: string,
): boolean => {
  const scopeSplit = control.scope.split('/');
  const name = scopeSplit[scopeSplit.length - 1];
  return name.toLowerCase().includes(value.replace(/\s/g, ''));
};

export const getControlsCount = (layout: Layout) => {
  const countRef = { count: 0 };
  getCount(layout, countRef);
  return countRef.count;
};

const getCount = (layout: Layout, countRef: { count: number }) => {
  layout.elements &&
    layout.elements.forEach((element) => {
      if (controls.includes(element.type)) {
        countRef.count++;
      } else {
        getCount(element as Layout, countRef);
      }
    });
};

export const handleFilterForm = (uiSchema: JsonSchema, value: string) => {
  return filterUiSchemaControls(uiSchema, value);
};

/**
 * format field path to match instancePath(returned from usevalidation) from 'app.name' to '/app/name'
 * @param instancePath
 */

export const setInstancePathFormat = (instancePath: string) => {
  return `/${instancePath.split('.').join('/')}`;
};

export const setSchemasFieldErrors = (errors: ErrorSchema[], path: string) => {
  const filedError = errors?.find(
    (err: ErrorSchema) => err?.instancePath === setInstancePathFormat(path),
  );
  return filedError;
};

export const currencySymbol = (formData: any, countryPath: string): string => {
  const country = get(formData, countryPath);

  return (currencies as any)[country]?.symbol || '$';
};

export const currencySymbolDirectFromCountry = (country: string): string => {
  return (currencies as any)[country]?.symbol || '';
};
